/**
 * Every feel number in one place. Units are world pixels and ticks (60 per second). Starting
 * values from the GDD; the prototype exists to tune these on a real phone.
 */
export const TUNING = {
  gravity: 0.24,
  maxFall: 5,

  runMax: 2,
  groundAccel: 0.3,
  airAccel: 0.2,
  groundFriction: 0.3,
  airFriction: 0.05,

  /** Peak height = v² / 2g = 48 px (3 tiles). */
  jumpVelocity: -4.8,
  /** Releasing jump while rising multiplies the upward speed by this, once (short hops). */
  jumpCut: 0.45,
  coyoteTicks: 6,
  jumpBufferTicks: 6,

  /** Jetpack: held jump in the air once the rise has slowed to this speed. */
  thrustEngageVy: -1,
  jetAccel: 0.42,
  jetMaxRise: -2.2,
  jetDrainPerTick: 0.22,
  /** In the Low power state the jet cuts out after this many ticks per airtime. */
  lowJetTicks: 40,

  /** Flutter (Yoshi-style): once per airtime, while held, at low power. */
  flutterTicks: 36,
  flutterLift: 0.5,
  flutterMaxRise: -0.6,
  flutterDrainPerTick: 0.03,

  starlightMax: 100,
  chargedAt: 60,
  lowAt: 20,
  regenPerTickGrounded: 0.04,
  cellStarlight: 25,
  dropStarlight: 8,

  fireCooldownTicks: 10,
  laserSpeed: 6,
  laserLifeTicks: 50,
  aimRange: 220,

  /** Rockets: hold FIRE this long (0.3 s), then release. Charged power only. */
  rocketChargeTicks: 18,
  rocketCost: 3,
  rocketSpeed: 3.5,
  rocketLifeTicks: 80,
  blastRadius: 26,
  rocketDamage: 3,

  /** Dash: a short burst, invulnerable while it lasts. Once per airtime. */
  dashSpeed: 5,
  dashTicks: 10,
  dashCooldownTicks: 20,
  dashCost: 5,

  armorMax: 3,
  invulnTicks: 60,
  knockbackVx: 2.5,
  knockbackVy: -3,

  /**
   * Dust vents (Mars): an updraft column above each vent, this many tiles tall (or up to the
   * first solid tile). Inside it the suit is pushed up for free: no Starlight spent.
   */
  ventRows: 8,
  ventLift: 0.5,
  ventMaxRise: -2.8,

  /**
   * Radiation zones above the suit's shielding cost a plate per this many ticks inside (one a
   * second), twice as fast with no Starlight left; the tank doesn't refill inside. Outside, the
   * dose wears off as fast.
   */
  radiationTicks: 60,

  /** Weapon mods: the spread laser's fan (radians either side) and how fast homing rockets turn. */
  spreadAngle: 0.22,
  homingTurn: 0.08,

  crawlerSpeed: 0.45,
  crawlerHp: 3,

  /** Drones hover near home, drift toward the player, glow for droneAimTicks, then fire a bolt. */
  droneHp: 2,
  droneSpeed: 0.8,
  droneSight: 180,
  droneKeepAway: 56,
  droneLeash: 96,
  droneFireEvery: 100,
  droneAimTicks: 36,
  boltSpeed: 2.2,
  boltLifeTicks: 150,

  /**
   * The Ranger (Mars boss): stalks from another lane, flashes, hops into the player's lane and
   * charges. Shots only land while it is dazed after a charge.
   */
  rangerHp: 36,
  rangerRun: 1.7,
  rangerIntroTicks: 90,
  /** How far behind the player it lands when it hops in. */
  rangerAmbushGap: 44,
  rangerWindupTicks: 16,
  rangerChargeMaxTicks: 90,
  rangerSpread: 0.28,
} as const;

/** Light-speed transit (the side-scrolling shoot-'em-up between systems). */
export const TRANSIT = {
  /** 60 seconds (GDD: 60–90). */
  durationTicks: 60 * 60,
  height: 270,
  shipRadius: 4,
  /** Dragging moves the ship toward the finger's target this fast; keys move it shipKeySpeed. */
  shipSpeed: 6,
  shipKeySpeed: 3,
  fireEvery: 9,
  laserSpeed: 7,
  bigRockRadius: 14,
  smallRockRadius: 7,
  bigRockHp: 4,
  raiderHp: 2,
  raiderSpeed: 1.6,
  /** Each raider fires at most once per this many ticks, and not when already close. */
  raiderFireEvery: 180,
  boltSpeed: 2,
  crystalStarlight: 10,
  invulnTicks: 60,
} as const;

/** The Ranger gets faster and meaner as its health drops: phase 1 above 2/3, phase 3 below 1/3. */
export interface RangerPhase {
  readonly stalkTicks: number;
  readonly tellTicks: number;
  readonly chargeSpeed: number;
  /** Charges per ambush: in phase 3 it turns at the wall and comes back. */
  readonly charges: number;
  readonly recoverTicks: number;
  /** Bolts fired in a fan as it recovers from a charge. */
  readonly volley: number;
}

export const RANGER_PHASES: readonly [RangerPhase, RangerPhase, RangerPhase] = [
  { stalkTicks: 150, tellTicks: 42, chargeSpeed: 3.4, charges: 1, recoverTicks: 80, volley: 0 },
  { stalkTicks: 115, tellTicks: 34, chargeSpeed: 3.8, charges: 1, recoverTicks: 64, volley: 3 },
  { stalkTicks: 85, tellTicks: 26, chargeSpeed: 4.2, charges: 2, recoverTicks: 52, volley: 5 },
];
